// Process map tab. Renders the MU's flowText via the existing FlowCanvas
// when present; otherwise shows a "Generate process map" call-to-action.

import React, { useMemo } from 'react';
import { Sparkles, RotateCcw, Workflow } from 'lucide-react';
import FlowCanvas from './FlowCanvas';
import type { DiscoveryFeature, MigrationUnit } from '../hooks/sessionTypes';
import type { NewModel } from '../utils/flowModel';

interface Props {
  mu: MigrationUnit;
  features: DiscoveryFeature[];
  hasFlow: boolean;
  generating: boolean;
  onGenerate: () => Promise<void>;
}

const MigrationUnitProcessMap: React.FC<Props> = ({ mu, features, hasFlow, generating, onGenerate }) => {
  const newModel: NewModel = useMemo(() => ({
    flowText: mu.flowText || '',
    openQuestions: '',
    nextQuestion: '',
    raw: mu.flowText || '',
    frozenBoundary: 0,
  }), [mu.flowText]);

  if (!hasFlow) {
    return (
      <div className="mu-tab-body mu-flow-empty">
        <Workflow size={36} className="mu-empty-icon" />
        <h2>No process map yet</h2>
        <p>
          The AI will read this MU's {features.length} feature{features.length === 1 ? '' : 's'}
          {' '}and produce a flow diagram describing the end-to-end journey.
        </p>
        <button
          type="button"
          className="dv-btn dv-btn--primary dv-btn--lg"
          onClick={onGenerate}
          disabled={generating || features.length === 0}
        >
          {generating ? <span className="dv-spinner" /> : <Sparkles size={14} />}
          {generating ? 'Generating…' : 'Generate process map'}
        </button>
        {features.length === 0 && (
          <p className="mu-prose-empty">This MU has no features yet — add some before generating a flow.</p>
        )}
      </div>
    );
  }

  return (
    <div className="mu-flow-wrap">
      <header className="mu-flow-toolbar">
        <div className="mu-flow-meta">
          <Workflow size={13} />
          <span>Process map</span>
          {mu.flowGeneratedAt && (
            <span className="mu-flow-time">generated {shortAgo(mu.flowGeneratedAt)}</span>
          )}
        </div>
        <button
          type="button"
          className="dv-btn dv-btn--ghost"
          onClick={() => {
            if (!window.confirm('Regenerate this process map? Any manual edits to the flow text will be lost.')) return;
            onGenerate();
          }}
          disabled={generating}
        >
          {generating ? <span className="dv-spinner" /> : <RotateCcw size={13} />}
          Regenerate
        </button>
      </header>
      <div className="mu-flow-canvas">
        <FlowCanvas newModel={newModel} />
      </div>
    </div>
  );
};

export default MigrationUnitProcessMap;

function shortAgo(iso: string): string {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return 'just now';
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return iso; }
}
